import React, { useEffect, useState } from 'react'
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import Button from '../../../../App/Components/Button/Button'
import Modal from '../../../../App/Components/Modal/Modal'
import Spinner from '../../../../App/Components/Spinner/Spinner'
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../App/Hooks/reduxHooks'
import { SurveyObject, ISurveyQuestion } from '../../Client/ISurvey'
import {
  addQuestion,
  deleteSurvey,
  getSurveys,
  updateQuestion,
  updateQuestionsOrder,
} from '../../Store/Action'
import { EditDescriptionCard } from '../Components'
import cardStyle from '../Components/cardStyle.scss'
import { MemoizedEditQuestionCard } from '../Components/MemoizedEditQuestionCard/MemoizedEditQuestionCard'

import { Layout } from './Layout'

type EditSurveyModalProp = {
  modalState: boolean
  setModalState: (state: boolean) => void
  passQuestionId?: string
}

const EditSurveyModal = ({
  modalState,
  setModalState,
}: EditSurveyModalProp) => {
  const { surveyId } = useParams<{ surveyId: string }>()
  const history = useHistory()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const confirmDelete = () => {
    dispatch(deleteSurvey(surveyId))
    setModalState(false)
    history.push(`/customers/surveys`)
  }

  return (
    <Modal
      modalState={modalState}
      setModalState={state => setModalState(state)}
      modalTitle={t('Delete Survey')}>
      <div>
        <p>{t('Are you sure you want to delete this survey?')}</p>
      </div>
      <div className={cardStyle.modalButtons}>
        <Button onClick={() => setModalState(false)}>{t('Cancel')}</Button>
        <Button color={'#333'} onClick={() => confirmDelete()}>
          {t('Delete')}
        </Button>
      </div>
    </Modal>
  )
}

export const EditSurvey = () => {
  const [modalState, setModalState] = useState<boolean>(false)
  const [questions, setQuestions] = useState<ISurveyQuestion[]>([])
  const [activeCard, setActiveCard] = useState<string>()

  const { surveyId } = useParams<{ surveyId: string }>()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const survey = useAppSelector(state => {
    if (state.customers.surveys.list) {
      return state.customers.surveys.list.find(
        (oneSurvey: SurveyObject) => oneSurvey.id === surveyId,
      )
    } else {
      dispatch(getSurveys())
    }
  })

  useEffect(() => {
    if (survey) {
      const sortedQuestions = [...survey.questions].sort(
        (a, b) => questions.indexOf(a) - questions.indexOf(b),
      )
      setQuestions(sortedQuestions)
    }
  }, [survey])

  const onDragEnd = (result: DropResult) => {
    // return if item was dropped outside
    if (!result.destination) return

    // return if the item was dropped in the same place
    if (
      result.source.droppableId === result.destination.droppableId &&
      result.source.index === result.destination.index
    ) {
      return
    }

    const sourceIndex = result.source.index
    const destinationIndex = result.destination.index
    const newQuestionOrder = [...questions]
    const deletedItems = newQuestionOrder.splice(sourceIndex, 1)
    newQuestionOrder.splice(destinationIndex, 0, ...deletedItems)

    // update state of dragAnd drop && order
    const orderedQuestionIds = [...newQuestionOrder].map(
      question => question.id,
    )

    dispatch(updateQuestionsOrder(questions, orderedQuestionIds, surveyId))
    setQuestions(newQuestionOrder)
  }

  useEffect(() => {
    if (survey) {
      setQuestions(survey.questions)
    }
  }, [survey])

  const addNewSurveyQuestion = () => {
    const amountOfQuestions = questions.length
    dispatch(
      addQuestion({
        surveyId,
        question: t('New Question {{count}}', { count: amountOfQuestions + 1 }),
        order: amountOfQuestions + 1,
        isMultiline: true,
        isMultipleChoice: false,
      }),
    )

  }

  const cardInEditMode = (cardId: string) => {
    setActiveCard(cardId)
  }

  const updateQuestionCard = (newQuestion: ISurveyQuestion) => {
    dispatch(updateQuestion(newQuestion, surveyId))
  }

  if (!survey) return <Spinner />

  return (
    <Layout>
      <EditSurveyModal
        modalState={modalState}
        setModalState={state => setModalState(state)}
      />
      <div>
        {survey && (
          <EditDescriptionCard
            survey={survey}
            onFocus={selectedCard => cardInEditMode(selectedCard)}
            isActive={surveyId === activeCard}
          />
        )}

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={'dropzone'}>
            {(provided: any) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {[...questions]
                  .sort((qA, qB) => qA.order - qB.order)
                  .map(question => (
                    <MemoizedEditQuestionCard
                      key={question.id}
                      isActive={question.id === activeCard}
                      question={question}
                      onFocus={selectedCard => cardInEditMode(selectedCard)}
                      onBlur={questionObject => {
                        if (questionObject) {
                          updateQuestionCard(questionObject)
                        }
                      }}
                      onKeyPress={keyEvent =>
                        keyEvent.key === '13'
                          ? keyEvent.currentTarget.blur()
                          : true
                      }
                    />
                  ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <div>
          <Button onClick={() => addNewSurveyQuestion()}>
            {t('Add New Question')}
          </Button>
          <Button
            buttonStyle={'destructive'}
            onClick={() => setModalState(true)}>
            {t('Delete survey')}
          </Button>
        </div>
      </div>
    </Layout>
  )
}
